import { useEffect, useMemo, useReducer, useCallback } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, FormControl, InputLabel, Select, MenuItem, Stack, Button, CircularProgress, Switch, FormControlLabel, Divider } from '@mui/material';
import { toast } from 'react-toastify';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RolePermissionService, RolePermissionActionService, ValidRoleService } from '../../../service/validDataService';
import { PermissionCatalogService } from '../../../service/permissionCatalogService';
import { AuthService } from '../../../service/authService';
import useAbortController from '../../../hooks/useAbortController';

// Dynamic: Sections & actions now come from backend catalog (/auth/action-catalog)

const ACCESS_LEVELS = [
  { id: 'none', label: 'No Access' },
  { id: 'read', label: 'Read' },
  { id: 'write', label: 'Read/Write' },
];

export default function RolePermissions() {
  const [state, setState] = useReducer((s, a) => ({ ...s, ...a }), {
    roles: [],
    selectedRoleId: '',
    permissions: {}, // { sectionId: accessLevel }
    actionPermissionsBySection: {}, // { [sectionKey]: { [actionKey]: boolean } }
    catalog: [], // fetched sections + actions
    catalogLoading: false,
    loading: false,
    saving: false,
  });

  // useAbortController returns [signal, abort]; we only need abort
  const [, abortFn] = useAbortController();
  const abort = useCallback(() => abortFn && abortFn(), [abortFn]);

  // Load roles for selection
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await ValidRoleService.getAll();
        if (!mounted) return;
        setState({ roles: data || [] });
      } catch { /* handled globally by interceptor */ }
    })();
    return () => { mounted = false; abort(); };
  }, [abort]);

  // Load permissions when role changes
  useEffect(() => {
    if (!state.selectedRoleId) return;
    let mounted = true;
    setState({ loading: true });
    (async () => {
      try {
        const { data } = await RolePermissionService.getForRole(state.selectedRoleId);
        if (!mounted) return;
        const map = {};
        (data || []).forEach(p => { map[p.section] = p.accessLevel; });
        setState({ permissions: map, loading: false });
      } catch {
        if (!mounted) return;
        setState({ loading: false });
      }
    })();
    return () => { mounted = false; abort(); };
  }, [state.selectedRoleId, abort]);

  // Fetch catalog (sections + actions)
  useEffect(() => {
    let mounted = true;
    setState({ catalogLoading: true });
    PermissionCatalogService.get()
      .then(res => {
        if (!mounted) return;
        // res.data.sections: [{ key, name, actions: [{ key, label }] }]
        setState({ catalog: res.data.sections || [], catalogLoading: false });
      })
      .catch(() => mounted && setState({ catalogLoading: false }));
    return () => { mounted = false; };
  }, []);

  // Compute stable list of active sections (only changes when sections become active/inactive)
  const activeSections = useMemo(() => {
    if (!state.catalog.length) return [];
    return state.catalog
      .map(s => s.key)
      .filter(key => (state.permissions[key] || 'none') !== 'none')
      .sort(); // Sort for stable comparison
  }, [state.catalog, state.permissions]);

  // Stable key that only changes when the SET of active sections changes
  const activeSectionsKey = useMemo(() => activeSections.join(','), [activeSections]);

  // Load action-level permissions generically for any section with access != none
  useEffect(() => {
    if (!state.selectedRoleId || !activeSections.length) {
      if (state.selectedRoleId && state.catalog.length && Object.keys(state.actionPermissionsBySection).length > 0) {
        // Clear action permissions if no sections are active
        setState({ actionPermissionsBySection: {} });
      }
      return;
    }
    
    let cancelled = false;
    (async () => {
      try {
        // Fetch all section actions in a single API call
        const resp = await RolePermissionActionService.getBulkForRole(state.selectedRoleId, activeSections);
        const bulkData = resp?.data || {};
        
        if (cancelled) return;
        
        // Transform backend format to UI format: { sectionKey: { actionKey: boolean } }
        const updates = {};
        activeSections.forEach(sectionKey => {
          const actions = bulkData[sectionKey] || [];
          const map = {};
          actions.forEach(a => { map[a.action] = !!a.allowed; });
          updates[sectionKey] = map;
        });
        
        setState({ actionPermissionsBySection: updates });
      } catch (err) {
        // On error, initialize with empty maps for each section
        if (cancelled) return;
        const updates = {};
        activeSections.forEach(sectionKey => { updates[sectionKey] = {}; });
        setState({ actionPermissionsBySection: updates });
      }
    })();
    
    return () => { 
      cancelled = true; 
    };
    // We intentionally depend on activeSectionsKey (not activeSections array) to avoid re-fetching
    // when unrelated permission levels change (e.g., read ↔ write for same section).
    // The key only changes when sections are added/removed from active set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedRoleId, activeSectionsKey]);

  // Map catalog to UI shape
  const sections = useMemo(() => state.catalog.map(s => ({ id: s.key, label: s.name || s.key, actions: s.actions || [] })), [state.catalog]);

  const handleAccessChange = (sectionId, value) => {
    const oldLevel = state.permissions[sectionId] || 'none';
    
    // Clear action permissions when downgrading from 'write' to non-'write'
    const shouldClearActions = oldLevel === 'write' && value !== 'write';
    
    setState({
      permissions: { ...state.permissions, [sectionId]: value },
      ...(shouldClearActions && {
        actionPermissionsBySection: {
          ...state.actionPermissionsBySection,
          [sectionId]: {} // Clear all action grants for this section
        }
      })
    });
  };

  const handleSave = async () => {
    if (!state.selectedRoleId) return;
    setState({ saving: true });
    try {
      // Save section-level permissions
      const payload = sections.map(s => ({ section: s.id, accessLevel: state.permissions[s.id] || 'none' }));
      await RolePermissionService.upsertForRole(state.selectedRoleId, payload);
      
      // Save per-section actions in bulk (only for write-level sections)
      const sectionActions = {};
      for (const section of sections) {
        const access = state.permissions[section.id] || 'none';
        if (access !== 'write' || !section.actions.length) continue; // only persist explicit action grants for write-level sections with defined actions
        const currentMap = state.actionPermissionsBySection[section.id] || {};
        sectionActions[section.id] = section.actions.map(a => ({ action: a.key, allowed: !!currentMap[a.key] }));
      }

      // Only call bulk API if there are actions to save
      if (Object.keys(sectionActions).length > 0) {
        await RolePermissionActionService.upsertBulkForRole(state.selectedRoleId, sectionActions);
      }
      
      toast.success('Permissions saved successfully');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save permissions');
    } finally {
      setState({ saving: false });
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Stack direction="row" gap={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="role-select-label">Role</InputLabel>
          <Select
            labelId="role-select-label"
            label="Role"
            value={state.selectedRoleId}
            onChange={(e) => setState({ selectedRoleId: e.target.value })}
          >
            {state.roles.map(r => (
              <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleSave} disabled={!state.selectedRoleId || state.saving}>
          {state.saving ? <CircularProgress size={20} /> : 'Save Permissions'}
        </Button>
        <Button
          variant="outlined"
          disabled={!state.selectedRoleId || state.saving}
          onClick={async () => {
            setState({ saving: true });
            try {
              await AuthService.refreshPermissionsCache('role');
              const { data } = await RolePermissionService.getForRole(state.selectedRoleId);
              const map = {}; (data || []).forEach(p => { map[p.section] = p.accessLevel; });
              setState({ permissions: map });
              toast.success('Permission cache refreshed');
            } catch (e) {
              toast.error('Failed to refresh permission cache');
            } finally {
              setState({ saving: false });
            }
          }}
        >
          Refresh Cache
        </Button>
      </Stack>

      {!state.selectedRoleId && (
        <Typography variant="body2" color="text.secondary">Select a role to configure permissions.</Typography>
      )}

      {state.selectedRoleId && (
        <Box>
          {sections.map(section => (
            <Accordion key={section.id} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{section.label}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id={`access-${section.id}`}>Access</InputLabel>
                  <Select
                    labelId={`access-${section.id}`}
                    label="Access"
                    value={state.permissions[section.id] || 'none'}
                    onChange={(e) => handleAccessChange(section.id, e.target.value)}
                  >
                    {ACCESS_LEVELS.map(a => (
                      <MenuItem key={a.id} value={a.id}>{a.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {section.actions?.length > 0 && (state.permissions[section.id] || 'none') !== 'none' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{section.label} actions</Typography>
                    <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                      {section.actions.map(a => (
                          <FormControlLabel
                              key={a.key}
                              control={
                                <Switch
                                  size="small"
                                  checked={!!(state.actionPermissionsBySection[section.id] || {})[a.key]}
                                  disabled={(state.permissions[section.id] || 'none') !== 'write'}
                                  onChange={(e) => setState({ actionPermissionsBySection: { ...state.actionPermissionsBySection, [section.id]: { ...(state.actionPermissionsBySection[section.id] || {}), [a.key]: e.target.checked } } })}
                                />
                              }
                              label={a.label || a.key}
                            />                          
                      ))}
                    </Stack>
                    {(state.permissions[section.id] || 'none') !== 'write' && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Enable Read/Write to configure {section.label.toLowerCase()} actions.
                      </Typography>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}
